<script setup lang="ts">
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { formatDurationLabel, formatDurationValue, mapSelectionToMinutes } from "@/utils/BookingStepperUtil";
import { requiredValidator } from "@validators";
import { VForm } from "vuetify/components";
import { useTodPricing } from "@/store/useTodPricing";
import type { BookingStepProps } from "../types/index";

const props = defineProps<
  BookingStepProps & {
    timeList?: any[];
    durationList?: any[];
    memberList?: any[];
    areaList?: any[];
    getAreaList?: any[];
    membership?: any;
    defaultArea?: string;
    allowedDatesRecurring?: (date: any) => boolean;
  }
>();

const emit = defineEmits<{
  (e: "update:form", value: any): void;
  (e: "validate"): Promise<boolean>;
  (e: "updateErrorMessage", value: any): void;
  (e: "loadUsersList", value: boolean): void;
}>();
const dateTimeFormRef = ref<InstanceType<typeof VForm>>();
const isLeftHand = ref(false);

const filteredAreaList = ref<any[]>([]);
const selectedTime = ref<any>({
  value: null,
  areaId: null,
});
const getSetupStore = useGlobalSetupStore();
const { globalSetup } = storeToRefs(getSetupStore);
const todStore = useTodPricing();
const { isTimeWithinTOD } = todStore;

// Computed
const selectedActivity = computed(() => {
  // Return first activity if form.activity is empty, otherwise find by id
  return !props.form.activity
    ? props.activityList?.[0]
    : props.activityList?.find((el) => el.id === props.form.activity);
});

const getBookingSpacing = computed(
  () => selectedActivity.value?.sessionDuration || 60,
);

const showLeftHand = computed(() => {
  // Only show left hand option if some areas don't support left-handed players
  return props.getAreaList?.some((area) => !area.canLeftHanded);
});

const times = ref<any[]>([]);

const getDurationList = computed(() => {
  if (!props.form.activity) return [];

  let gap = selectedActivity.value?.bookingGap || 0
  let slotLimit = selectedActivity.value?.sessionLimit || 1
  const durationList = [];
  const numberOfSlots = Math.min(slotLimit);

  for (let i = 0; i < numberOfSlots; i++) {
    const durationMinutes = (i + 1) * getBookingSpacing.value;
    durationList.push({
      value: formatDurationValue(durationMinutes, getBookingSpacing.value % 60 === 0),
      text: formatDurationLabel(durationMinutes - gap),
    });
  }
  return durationList;
});

const isSlotInTOD = (item: any) => !!getSlotTOD(item);

const getSlotTOD = (item: any) => {
  const todPricing = selectedActivity.value?.expand?.TODPricing;
  if (!todPricing?.length) return null;
  const slotTime = moment(
    `${moment(props.form.date).format("YYYY-MM-DD")} ${item.value}`,
    "YYYY-MM-DD HH:mm:ss"
  );
  const applicable = todPricing.filter((tod: any) => isTimeWithinTOD(tod, slotTime));
  if (!applicable.length) return null;
  return applicable.reduce((best: any, tod: any) => tod.percentChange < best.percentChange ? tod : best);
};

const getBookingList = async (date: any) => {
  const _date = typeof date === "string" ? date : moment(date).format("YYYY-MM-DD");
  const nzStartDate = moment(_date)
    .startOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");
  const nzEndDate = moment(_date)
    .endOf("day")
    .utc()
    .format("YYYY-MM-DD HH:mm:ss");
  // Get all bookings from today onwards
  const bookings = await pb.collection("bookings").getFullList({
    filter: `startTime >= "${nzStartDate}" && startTime <= "${nzEndDate}" && state != "Deleted"`,
  });
  filteredAreaList.value = [...(props.getAreaList || [])];
  // Process each area in the list
  filteredAreaList.value.forEach((area: any) => {
    // Filter bookings for this specific area
    const areaBookings = bookings.filter((book: any) => book.area === area.id);

    // Initialize timeSlot array for this area
    area.timeSlot = [];

    // Process each booking for this area
    areaBookings.forEach((book: any) => {
      const startTime = moment(book.startTime);
      const bookingSpacing = selectedActivity.value?.bookingSpacing || 60; // Default to 60 minutes if due is not set
      const slots = Math.ceil(book.duration / bookingSpacing);

      // Generate time slots for the booking duration
      for (let i = 0; i < slots; i++) {
        const slotTime = startTime
          .clone()
          .add(i * bookingSpacing, "minutes")
          .format("HH:mm");
        area.timeSlot.push(slotTime);
      }
    });
  });
};

onMounted(() => {
  // Run getBookingList on component mount
  getBookingList(props.form.date);
    let valid = false
    getDurationList.value.forEach(dur => {
      if(props.form.duration == dur.value){
        valid = true
      }
    })
    if(!valid) {
      props.form.duration = getDurationList.value[0]?.value || 1
    }

  getDurationList.value.forEach((durObj:any) => {
    let dur = durObj.value.toString()
    let num = ''
    let currentDur = props.form.duration.toString()
    if(currentDur.indexOf('.') != -1){ // It is in Decimal
      let parts = currentDur.split(".").pop()
      let decimalToMins = parseFloat(parseInt(0) + "." + parts) * 60
      num = currentDur.split(".")[0] + ":" + decimalToMins
    }
    if(currentDur == dur || currentDur + ":00" == dur || num == dur ){
      props.form.duration = dur
    }
  })

});

const debounce = (fn: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Create debounced version of getBookingList
const debouncedGetBookingList = debounce(getBookingList, 300);

const handleDateChange = (date: any) => {
  selectedTime.value = {
    value: null,
    areaId: null,
  };
  emit("update:form", {
    ...props.form,
    date: date,
    time: null,
    selectedOptions: [],
  });
  debouncedGetBookingList(date);
};

const handleDurationChange = (duration: any) => {
  selectedTime.value = {
    value: null,
    areaId: null,
  };
  if (duration && duration.value) {
    emit("update:form", {
      ...props.form,
      duration: duration.value,
      time: null,
    });
  } else {
    emit("update:form", {
      ...props.form,
      duration: duration,
      time: null,
      selectedOptions: [],
    });
  }
};

const handleTimeChange = (time: any) => {
  selectedTime.value = time;
  emit("update:form", {
    ...props.form,
    time: time.value,
    area: time.areaId,
    selectedOptions: [],
  });
};

// Watch for changes in relevant dependencies
watch(
  [
    () => props.form.date,
    () => props.form.duration,
    () => isLeftHand.value,
    () => filteredAreaList.value,
    () => props.form.activity,
  ],
  async () => {
    times.value = [];
    if (!props.form.date) return;
    const currentDate = moment().startOf("day");
    const selectedDate = moment(props.form.date).startOf("day");

    if (selectedDate.isBefore(currentDate)) {
      times.value = [];
      return;
    }

    const interval = selectedActivity.value?.bookingSpacing || getBookingSpacing.value
    const isToday = selectedDate.isSame(currentDate, "day");

    const formatTimeTo12Hour = (hour: any, min: any) => {
      return moment(`${hour}:${min}`, "HH:mm").format("hh:mm A");
    };

    const response = await pb.send('/bbapi/times/getAvailableTimes', {
      method: 'POST',
      body: {
        activity: selectedActivity.value?.id,
        membership: props?.membership || [],
        date: selectedDate.isValid() ? selectedDate.format('YYYY-MM-DD') : currentDate.format('YYYY-MM-DD'),
      },
      requestKey: null,
    });
    // Only use first time that matches
    const availableTimes = response?.availableTimes?.length ? [response.availableTimes[0]] : []

    // Create available times for all areas
    const areaAvailability: { areaId: string; availableTimes: string[] }[] = [];

    const areaList = filteredAreaList.value?.filter((el: any) => {
      if (isLeftHand.value === true) {
        return el.canLeftHanded;
      } else {
        return true;
      }
    });
    // Calculate available times for each area
    (areaList || []).forEach((area: any) => {
      const availableTimesForArea = [];

      const currentTimeInMinutes = isToday
        ? moment().hours() * 60 + moment().minutes()
        : 0;


      let startMins = 0
      startMins = area?.offSetMinutes || 0

      // Generate all possible time slots
      for (let hour = 0; hour < 24; hour++) {
        for (let min = startMins; min < 60; min += interval) {
          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          const time = `${formattedHour}:${formattedMin}`;

          const timeInMinutes = hour * 60 + min;

          // Skip past times including buffer
          if (isToday && timeInMinutes <= currentTimeInMinutes) {
            continue;
          }

          // Check if the time slot is available in activity hours
          const isAvailable = availableTimes.some((timeSlot: any) => {
            const startTime = timeSlot.startHour * 60 + timeSlot.startMin;
            let endTime = timeSlot.finishHour * 60 + timeSlot.finishMin;

            if(interval != getBookingSpacing.value && (hour + (min / 60)) * getBookingSpacing.value > endTime - interval - interval){
              return false
            }

            if(startMins > 0){
              endTime = endTime - interval // Take Interval off last slot
            }

            if (startTime === 0 && endTime === 0) return true;
            else if (startTime === 0) return timeInMinutes < endTime;
            else if (endTime === 0) return timeInMinutes >= startTime;

            if (endTime > startTime) {
              return timeInMinutes >= startTime && timeInMinutes < endTime;
            }
            if (endTime <= startTime) {
              return timeInMinutes >= startTime || timeInMinutes < endTime;
            }
            return false;
          });
          // Check if time is not in area.timeSlot
          if (isAvailable && area.timeSlot && !area.timeSlot?.includes(time)) {
            availableTimesForArea.push(time);
          }
        }
      }

      areaAvailability.push({
        areaId: area.id,
        availableTimes: availableTimesForArea,
      });
    });

    // Find area with most available slots
    if (areaAvailability.length > 0) {
      // First collect all available time slots with their areas
      const allTimeSlots: { time: string; areaId: string }[] = [];

      // Process each area's available times
      areaAvailability.forEach(({ areaId, availableTimes }) => {
        const durationMinutes = mapSelectionToMinutes(
          String(props.form.duration ?? "0").replace(":", "."),
        );
        const durationSlots = Math.ceil(durationMinutes / interval);

        // Filter times based on duration for this area
        const filteredTimes = availableTimes.filter((time) => {
          if (!Number.isFinite(durationSlots) || durationSlots <= 1) return true;

          // Check consecutive slots
          const [hour, minute] = time.split(":").map(Number);
          const currentTimeInMinutes = hour * 60 + minute;

          // Verify all required consecutive slots
          for (let i = 1; i < durationSlots; i++) {
            const nextTimeInMinutes = currentTimeInMinutes + i * interval;
            const nextHour = Math.floor(nextTimeInMinutes / 60);
            const nextMinute = nextTimeInMinutes % 60;
            const nextTimeStr = `${nextHour
              .toString()
              .padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;

            if (!availableTimes.includes(nextTimeStr)) {
              return false;
            }
          }
          return true;
        });

        // Add each filtered time with its area to allTimeSlots
        filteredTimes.forEach((time) => {
          allTimeSlots.push({ time, areaId });
        });
      });

      // Group time slots by time
      const timeGroups = allTimeSlots.reduce(
        (groups, slot) => {
          if (!groups[slot.time]) {
            groups[slot.time] = [];
          }
          groups[slot.time].push(slot.areaId);
          return groups;
        },
        {} as Record<string, string[]>,
      );

      const nzStartDate = moment(props.form.date)
        .startOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      const nzEndDate = moment(props.form.date)
        .endOf("day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      let bookingsInPlace = await pb.collection("bookings").getFullList({
        filter: `startTime >= "${nzStartDate}" && startTime <= "${nzEndDate}" && state != "Deleted"`,
        fields: "id,area,startTime,endTime"
      })

      let durationMins =props?.form?.duration && props.form.duration.toString().split(":")[1] ? props.form.duration.toString().split(":")[1] : 0
      let durationHours = props?.form?.duration && props.form.duration.toString().split(":")[0] ? props.form.duration.toString().split(":")[0] : 0
      // Create final times array with unique times and random areas
      times.value = Object.entries(timeGroups).map(([time, areas]) => {
        const [hour, minute] = time.split(":");
        let tempAreas = areas.filter((a:any) => {
          let canAreaBeUsed = true
          bookingsInPlace.forEach(b => {
            if(b.area == a){
              if(b?.startTime && b?.startTime != '') {
                let st = moment(b.startTime)
                let et = moment(b.endTime)
                let bs = moment(props.form.date + " " + hour + ":" + minute + ":00")
                let be = moment(bs).add(durationHours, "hours").add(durationMins, "minutes")
                if(bs.isBetween(st,et) || be.isBetween(st,et)){
                  canAreaBeUsed = false
                }
              }
            }
          })
          return canAreaBeUsed
        })

        if(tempAreas.length > 0) {
          const randomAreaId = tempAreas[Math.floor(Math.random() * tempAreas.length)];
          return {
            label: formatTimeTo12Hour(hour, minute),
            value: time,
            isNormalHour: true,
            areaId: props.defaultArea && tempAreas.includes(props.defaultArea) ? props.defaultArea : randomAreaId,
          };
        }
      }).filter(t => t?.value);

      // Sort times chronologically
      times.value.sort((a, b) => a.value.localeCompare(b.value));
    }
  },
  { immediate: true, deep: false },
);

defineExpose({
  validate: async () => {
    // Add time validation check
    if (!selectedTime.value.value) {
      emit("updateErrorMessage", {
        isVisible: true,
        message: "Please select a booking time",
      });
      return false;
    }
    const results = await dateTimeFormRef.value?.validate();
    return results?.valid ?? false;
  },
});
</script>

<template>
  <VForm ref="dateTimeFormRef">
    <VRow>
      <!-- Date Picker -->
      <VCol lg="6" md="6" sm="6" xs="6" cols="12">
        <DateInput
          v-model="form.date"
          label="Date"
          placeholder="Select Date"
          :disabled="isFieldsDisabled"
          format="dddd, Do MMM YYYY"
          :rules="[requiredValidator]"
          :allowedDates="props.allowedDatesRecurring"
          @update:modelValue="handleDateChange"
        />
      </VCol>

      <!-- Duration Select -->
      <VCol lg="6" md="6" sm="6" xs="6" cols="12">
        <VSelect
          v-model="form.duration"
          :items="getDurationList"
          v-if="getDurationList.length"
          :rules="[requiredValidator]"
          item-title="text"
          item-value="value"
          label="Duration"
          @update:modelValue="handleDurationChange($event)"
        />
      </VCol>

      <VCol lg="6" md="6" sm="6" xs="6" cols="12" v-if="showLeftHand">
        <VCheckbox
          v-model="isLeftHand"
          label="Left handed player in group"
          color="primary"
          hide-details
          @update:modelValue="
            const area = props.areaList?.find((el) => el.id === form.area);
          "
        />
      </VCol>

      <!-- Time Select -->
      <VCol cols="12">
        <div v-if="times.length !== 0">
          <p>Choose from Available Booking Times:</p>
        </div>
        <div v-if="times.length !== 0">
          <div class="time-buttons-wrapper">
            <VBtn
              v-for="time in times"
              :key="time?.value"
              class="time-btn"
              :class="time.areaId === props.defaultArea ? 'highlighted' : ''"
              :color="selectedTime.value === time?.value ? 'primary' : 'secondary'"
              @click="handleTimeChange(time)"
            >
              <div v-if="isSlotInTOD(time)" class="mr-1">
                <v-icon icon="mdi-clock-outline"/>
                <VTooltip activator="parent" location="top">
                  Time Of Day Pricing {{ getSlotTOD(time)?.percentChange > 0 ? '+' : '' }}{{ getSlotTOD(time)?.percentChange }}%
                </VTooltip>
              </div>
              {{ time?.label }}
            </VBtn>
          </div>
          <p class="mt-2" v-if="props.defaultArea">Highlighted times are in your selected {{ globalSetup?.areaSingular.toLocaleLowerCase() || "bay" }}</p>
        </div>
        <div v-else class="error-message">
          <VIcon icon="mdi-alert-circle" />
          No bookings available for the selected date.
        </div>
      </VCol>
    </VRow>
  </VForm>
</template>

<style lang="scss" scoped>
.time-buttons-wrapper {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  inline-size: 100%;

  .highlighted:not(.bg-primary) {
    background-color: rgb(var(--v-theme-success)) !important;
  }
}

.time-btn {
  inline-size: 100%;
  min-inline-size: unset;
}
</style>
